/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import type { CurrencyCode } from '../types/currency';

interface CurrencyData {
  code: CurrencyCode;
  symbol: string;
  rate: number; // Rate relative to the base currency (EGP)
  locale: string;
}

const CURRENCIES: Record<CurrencyCode, CurrencyData> = {
  EGP: { code: 'EGP', symbol: 'EGP', rate: 1, locale: 'en-EG' },
  USD: { code: 'USD', symbol: '$', rate: 0.021, locale: 'en-US' },
  EUR: { code: 'EUR', symbol: '€', rate: 0.019, locale: 'de-DE' },
  SAR: { code: 'SAR', symbol: 'SAR', rate: 0.079, locale: 'ar-SA' },
  AED: { code: 'AED', symbol: 'AED', rate: 0.077, locale: 'en-AE' },
};

interface CurrencyContextType {
  currency: CurrencyCode;
  setCurrency: (code: CurrencyCode) => void;
  formatPrice: (price: number, options?: { compact?: boolean }) => string;
  convert: (price: number) => number;
  symbol: string;
  currencies: CurrencyCode[];
}

const CurrencyContext = createContext<CurrencyContextType | undefined>(undefined);

import { useCountry } from '../context/CountryContext'; 

export const CurrencyProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currency, setCurrencyState] = useState<CurrencyCode>('EGP');
  const { activeCountry } = useCountry();

  // 1. Initial Load from LocalStorage
  useEffect(() => {
    const savedCurrency = localStorage.getItem('protech_currency') as CurrencyCode;
    if (savedCurrency && CURRENCIES[savedCurrency]) {
      setCurrencyState(savedCurrency);
    }
  }, []);

  // 2. Sync with Country Context
  useEffect(() => {
    if (activeCountry && activeCountry.currency_code) {
        // Validate if we support this currency
        const code = activeCountry.currency_code as CurrencyCode;
        if (CURRENCIES[code]) {
            setCurrencyState(code);
            // Don't auto-save to localStorage here to avoid overriding user preference persistently if they just switch country
            // But for now, let's sync it.
            localStorage.setItem('protech_currency', code);
        }
    }
  }, [activeCountry]);

  const setCurrency = (code: CurrencyCode) => {
    setCurrencyState(code);
    localStorage.setItem('protech_currency', code);
  };

  const activeCurrencyData = CURRENCIES[currency];

  // Convert from Active Country's Currency -> Selected Display Currency
  const convert = useCallback((price: number): number => {
    if (!price) return 0;
    
    // Source is active country's currency (or EGP default)
    // We assume the Price stored in DB is in the Country's Local Currency.
    const sourceCode = (activeCountry?.currency_code || 'EGP') as CurrencyCode;
    const targetCode = currency;

    if (sourceCode === targetCode) return price;

    const sourceRate = CURRENCIES[sourceCode]?.rate || 1; // 1 EGP = X Source
    const targetRate = CURRENCIES[targetCode]?.rate || 1; // 1 EGP = Y Target

    // Convert Source -> EGP -> Target
    const priceInEgp = price / sourceRate;
    return priceInEgp * targetRate;
  }, [activeCountry?.currency_code, currency]);

  const formatPrice = useCallback((price: number, options: { compact?: boolean } = {}): string => {
    const convertedValue = convert(price);
    const { locale, code } = activeCurrencyData;

    const formattingOptions: Intl.NumberFormatOptions = {
      style: 'currency',
      currency: code,
    };

    if (options.compact) {
      formattingOptions.notation = "compact";
      formattingOptions.maximumFractionDigits = 2;
    } else {
      formattingOptions.minimumFractionDigits = 0;
      formattingOptions.maximumFractionDigits = 0;
    }
    
    return new Intl.NumberFormat(locale, formattingOptions).format(convertedValue);
  }, [convert, activeCurrencyData]);

  const value = useMemo(() => ({
    currency,
    setCurrency,
    formatPrice,
    convert,
    symbol: activeCurrencyData.symbol,
    currencies: Object.keys(CURRENCIES) as CurrencyCode[],
  }), [currency, activeCountry, activeCurrencyData.symbol, convert, formatPrice]); // Depend on activeCountry

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (context === undefined) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};
